// ============================================================================
// sync-folder-write-engine.js — DEC-COUNCIL-007 / Candidate C5 Slice 3
// ----------------------------------------------------------------------------
// The retry-aware write path the sync orchestrator funnels every blob through.
// Extracted from sync-folder.js so:
//
//   1. Retry tuning (delay schedule, attempt count, transient-error
//      classification) lives in one file.
//   2. The orchestrator becomes structurally smaller — sync-folder.js's
//      sequencing layer no longer carries the retry primitives.
//   3. Independent unit tests against a mock FileSystem can exercise the
//      retry behaviour without spinning up the full sync object.
//
// Pure: no module-level state. The classifier comes from file-system-errors.js
// (Slice 2 already consolidated). The FileSystem write call is injected as
// `writeFileToDirectory` so tests can swap in a fake without monkeypatching
// FileSystemService.
//
// See:
//   - docs/council/DECISION-LOG.md DEC-COUNCIL-007 Candidate C5 Slice 3
//   - CONTEXT.md → "Sync Folder Write Engine"
// ============================================================================

import { isTransientFileSystemError } from './file-system-errors.js';

/** Number of attempts the write engine retries before giving up. */
export const DEFAULT_WRITE_ATTEMPTS = 5;

function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Back-off schedule for in-folder writes (per-attempt delay before retry).
 * Modest growth so Chrome's folder-state race usually clears in 1-2 retries.
 */
export function childFileRetryDelay(attempt: number) {
    return Math.min(800, 90 * Math.pow(attempt + 1, 2));
}

/**
 * Back-off schedule for the project-directory resolution path.
 */
export function projectDirectoryRetryDelay(attempt: number) {
    return Math.min(1000, 120 * Math.pow(attempt + 1, 2));
}

/**
 * Best-effort permission refresh on a directory handle. Some Chrome versions
 * silently change permission state when the user switches profiles or relaunches;
 * a no-op call to queryPermission nudges the engine to re-evaluate.
 */
export async function refreshDirectoryHandleState(handle: any) {
    if (!handle) return;
    try {
        if (typeof handle.queryPermission === 'function') {
            await handle.queryPermission({ mode: 'readwrite' });
        }
    } catch { /* Permission state refresh is best-effort only. */ }
}

/**
 * Write a blob to a directory handle with retry-on-transient-error.
 *
 * @param {object} args
 * @param {FileSystemDirectoryHandle} args.directoryHandle
 * @param {string} args.fileName
 * @param {Blob} args.blob
 * @param {Function} args.writeFileToDirectory — injected (FileSystemService.writeFileToDirectory)
 * @param {FileSystemDirectoryHandle} [args.parentHandle] — for re-resolving the sub-directory
 * @param {string} [args.directoryName] — used with parentHandle to re-get the handle
 * @param {number} [args.attempts]
 * @returns {Promise<{ result: any, directoryHandle: FileSystemDirectoryHandle, recovered: boolean }>}
 */
export async function writeSyncFileWithRetry({
    directoryHandle,
    fileName,
    blob,
    writeFileToDirectory,
    parentHandle = null,
    directoryName = '',
    attempts = DEFAULT_WRITE_ATTEMPTS,
}: {
    directoryHandle: any;
    fileName: string;
    blob: Blob;
    writeFileToDirectory: any;
    parentHandle?: any;
    directoryName?: string;
    attempts?: number;
}) {
    if (typeof writeFileToDirectory !== 'function') {
        throw new TypeError('writeSyncFileWithRetry: writeFileToDirectory is required (inject FileSystemService.writeFileToDirectory)');
    }
    let activeDirectory = directoryHandle;
    let lastError: any = null;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
            const result = await writeFileToDirectory(activeDirectory, fileName, blob);
            return { result, directoryHandle: activeDirectory, recovered: attempt > 0 };
        } catch (err) {
            lastError = err;
            if (!isTransientFileSystemError(err) || attempt === attempts - 1) {
                throw err;
            }

            await delay(childFileRetryDelay(attempt));
            await refreshDirectoryHandleState(parentHandle || activeDirectory);

            if (parentHandle && directoryName) {
                try {
                    activeDirectory = await parentHandle.getDirectoryHandle(directoryName, { create: true });
                } catch (refreshErr) {
                    if (!isTransientFileSystemError(refreshErr) || attempt === attempts - 1) {
                        throw refreshErr;
                    }
                }
            }
        }
    }

    throw lastError || new Error(`Failed to write ${fileName}`);
}
